/**
 * HUD — 持久 DOM 覆盖层：回合胶囊 / 玩家面板 / 操作按钮 / Toast。
 * 只做展示与入口，规则判断一律归属场景层（合法点由规则决定而非 UI 猜测）。
 */
import type { DictKey } from './i18n';

export interface HudPlayer {
  seat: number;
  color: string;
  name: string;
  dest: number;
  active: boolean;
}

export interface TurnInfo {
  color: string;
  label: string;
  timerText: string | null;
}

export interface HudHandle {
  setTurn(info: TurnInfo | null): void;
  setChain(key: DictKey | null, t?: (k: DictKey) => string): void;
  setPlayers(players: HudPlayer[]): void;
  setHint(text: string | null): void;
  toast(text: string, ms?: number): void;
  dispose(): void;
}

export interface HudActions {
  onSettings: () => void;
  onSound: () => void;
  onHome: () => void;
  /** 切换调试日志窗口（v0.5）。 */
  onDebug: () => void;
  /** Muted state → speaker icon slash. */
  getMuted: () => boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  parent?: HTMLElement
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}

function iconSvg(path: string): string {
  return `<svg viewBox="0 0 24 24"><path d="${path}"/></svg>`;
}

const ICONS = {
  settings:
    'M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Zm7.4-3.5c0 .5 0 .9-.1 1.3l2 1.6-1.9 3.3-2.4-.9c-.7.5-1.4 1-2.2 1.2L14.4 21h-3.8l-.4-2.5c-.8-.3-1.5-.7-2.2-1.2l-2.4.9L3.7 15l2-1.6a8 8 0 0 1 0-2.7l-2-1.6L5.6 5.8l2.4.9c.7-.5 1.4-1 2.2-1.2L10.6 3h3.8l.4 2.5c.8.3 1.5.7 2.2 1.2l2.4-.9 1.9 3.3-2 1.6c.1.4.1.8.1 1.3Z',
  sound: 'M4 9.5h3.5L12 5v14l-4.5-4.5H4v-5Zm11.3-1.2a5 5 0 0 1 0 7.4m2.6-10a9 9 0 0 1 0 12.6',
  soundOff: 'M4 9.5h3.5L12 5v14l-4.5-4.5H4v-5Zm11 1 5 5m0-5-5 5',
  home: 'M4 11.5 12 4l8 7.5M6.5 10v9.5h11V10',
  debug:
    'M12 5.5a1 1 0 1 0-1-1 1 1 0 0 0 1 1Zm-3.2 1.1-1.9-1.9M15.2 6.6l1.9-1.9M5 11.5H2.5M21.5 11.5H19M6.5 8a5.5 5.5 0 0 0-.9 6.1m12.8-6.1a5.5 5.5 0 0 1 .9 6.1M12 20.5v-1m-5.5-1.9V21M17.5 17.6V21',
};

export function createHud(root: HTMLElement, actions: HudActions): HudHandle {
  // Top: turn capsule + chain banner
  const top = el('div', 'hud-top', root);
  const chainBanner = el('div', 'chain-banner', top);
  chainBanner.style.display = 'none';
  const capsule = el('div', 'capsule', top);
  capsule.style.display = 'none';
  const turnDot = el('span', 'dot', capsule);
  const turnLabel = el('span', '', capsule);
  const timerEl = el('span', 'timer', capsule);

  // Side: players
  const side = el('div', 'hud-side', root);
  side.style.display = 'none';

  // Bottom actions
  const bottom = el('div', 'hud-bottom', root);
  const soundBtn = el('button', 'icon-btn', bottom);
  soundBtn.title = 'Sound';
  const homeBtn = el('button', 'icon-btn', bottom);
  homeBtn.title = 'Home';
  const settingsBtn = el('button', 'icon-btn', bottom);
  settingsBtn.title = 'Settings';
  const debugBtn = el('button', 'icon-btn icon-btn--debug', bottom);
  debugBtn.title = 'Debug Log';
  soundBtn.innerHTML = iconSvg(actions.getMuted() ? ICONS.soundOff : ICONS.sound);
  homeBtn.innerHTML = iconSvg(ICONS.home);
  settingsBtn.innerHTML = iconSvg(ICONS.settings);
  debugBtn.innerHTML = iconSvg(ICONS.debug);
  settingsBtn.addEventListener('click', actions.onSettings);
  homeBtn.addEventListener('click', actions.onHome);
  debugBtn.addEventListener('click', actions.onDebug);
  soundBtn.addEventListener('click', () => {
    actions.onSound();
    soundBtn.innerHTML = iconSvg(actions.getMuted() ? ICONS.soundOff : ICONS.sound);
  });

  // Hint + toasts
  const hintEl = el('div', 'toast', root);
  hintEl.style.position = 'absolute';
  hintEl.style.bottom = '22px';
  hintEl.style.left = '50%';
  hintEl.style.transform = 'translateX(-50%)';
  hintEl.style.display = 'none';
  const toastWrap = el('div', 'toast-wrap', root);

  return {
    setTurn(info) {
      if (!info) {
        capsule.style.display = 'none';
        return;
      }
      capsule.style.display = '';
      turnDot.style.color = info.color;
      turnLabel.textContent = info.label;
      if (info.timerText !== null) {
        timerEl.style.display = '';
        timerEl.textContent = info.timerText;
      } else {
        timerEl.style.display = 'none';
      }
    },
    setChain(key, t) {
      if (key === null) {
        chainBanner.style.display = 'none';
        return;
      }
      chainBanner.style.display = '';
      chainBanner.textContent = t ? t(key) : key;
    },
    setPlayers(players) {
      side.innerHTML = '';
      side.style.display = players.length > 0 ? '' : 'none';
      for (const p of players) {
        const row = el('div', `player-row${p.active ? ' active' : ''}`, side);
        row.style.color = p.color;
        el('span', 'dot', row);
        const name = el('span', 'name', row);
        name.textContent = p.name;
        const count = el('span', 'count', row);
        count.textContent = `${p.dest}/10`;
      }
    },
    setHint(text) {
      if (text === null) {
        hintEl.style.display = 'none';
        return;
      }
      hintEl.style.display = '';
      hintEl.textContent = text;
    },
    toast(text, ms = 2200) {
      const t = el('div', 'toast', toastWrap);
      t.textContent = text;
      setTimeout(() => t.classList.add('out'), ms);
      setTimeout(() => t.remove(), ms + 350);
    },
    dispose() {
      top.remove();
      side.remove();
      bottom.remove();
      hintEl.remove();
      toastWrap.remove();
    },
  };
}
